
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { type Gift, type Brand } from '@/utils/types';

interface ExchangeActionProps {
  selectedGift: Gift;
  targetGift: Gift | null;
  brands: Brand[];
  onTargetGiftChange: (gift: Gift) => void;
}

const ExchangeAction: React.FC<ExchangeActionProps> = ({
  selectedGift,
  targetGift,
  brands,
  onTargetGiftChange
}) => {
  const getBrandName = (brandId: string) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : 'Unknown';
  };

  // Calculate fee and net amount
  const feeAmount = selectedGift.amount * 0.10;
  const netAmount = selectedGift.amount - feeAmount;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Exchange your GivGo Bucks for another brand.
      </p>
      <div className="space-y-4">
        <div className="flex items-center justify-center gap-4">
          <div className="text-center p-3 border rounded-lg bg-gray-50 flex-1">
            <p className="font-medium">{getBrandName(selectedGift.brandId)}</p>
            <p className="text-lg font-bold">${selectedGift.amount.toFixed(2)} GivGo Bucks</p>
          </div>
          
          <ArrowRight className="h-5 w-5 flex-shrink-0" />
          
          <div className="text-center p-3 border rounded-lg bg-blue-50 flex-1">
            <p className="font-medium">{targetGift ? getBrandName(targetGift.brandId) : 'Select brand'}</p>
            <p className="text-lg font-bold">${netAmount.toFixed(2)} GivGo Bucks</p>
          </div>
        </div>
        
        <div className="p-3 border rounded-lg bg-yellow-50 text-center">
          <p className="text-sm">10% transaction fee applies (${feeAmount.toFixed(2)})</p>
        </div>
        
        <div className="border rounded-lg overflow-hidden">
          <Command>
            <CommandInput placeholder="Search brands..." />
            <CommandList>
              <CommandEmpty>No brands found.</CommandEmpty>
              <CommandGroup>
                {brands.filter(b => b.id !== selectedGift.brandId).map(brand => (
                  <CommandItem 
                    key={brand.id}
                    onSelect={() => {
                      onTargetGiftChange({
                        id: `exchange-${Date.now()}-${brand.id}`,
                        brandId: brand.id,
                        amount: netAmount,
                        senderId: 'system',
                        recipientId: 'current-user',
                        createdAt: new Date(),
                        isRedeemed: false
                      });
                    }}
                  >
                    <div className="flex items-center">
                      {brand.logo && (
                        <img 
                          src={brand.logo} 
                          alt={brand.name} 
                          className="w-6 h-6 object-contain mr-2"
                        />
                      )}
                      {brand.name}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      </div>
    </div>
  );
};

export default ExchangeAction;
